<template>
    <v-container v-if="isVisible" class="pa-0">
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-title :text="t('travelCard.travelCardUpdateTitle')" />
            <input-field
                id="name"
                v-model="formData.name"
                data-id="travelCardEditFormNameField"
                :max="FIELD_RULES.CARD_NAME_MAX_CHAR"
                :min="FIELD_RULES.CARD_NAME_MIN_CHAR"
                :label="t('travelCard.travelCardNameField')"
                :required="true"
                :disabled="isSubmitting"
                @input="validateOnInput"
            />
            <v-card-actions class="d-flex justify-end ma-0 pa-0">
                <card-button
                    class="flex-1-0-0"
                    size="large"
                    :inactive-label="t('form.cancel')"
                    data-id="travelCardEditFormCancelButton"
                    @click="handleCancel"
                >
                    {{ t('form.cancel') }}
                </card-button>
                <card-button
                    variant="flat"
                    type="submit"
                    class="flex-1-0-0"
                    :disabled="!isFormValid"
                    size="large"
                    :inactive-label="t('form.save')"
                    :loading="isSubmitting"
                    data-id="travelCardEditFormSaveButton"
                >
                    {{ t('form.save') }}
                </card-button>
            </v-card-actions>
        </v-form>
        <v-divider class="grey lighten-2 mb-0 mt-5"></v-divider>
    </v-container>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { UpdateTravelCardDto, IFormProps } from '@/features/TravelCards/types';
import { updateTravelCard } from '../../../services/api';
import CardButton from '@/features/common/components/CardButton.vue';
import { InputField, FormTitle, NotificationBar } from '@/features/common/components/form';
import { FIELD_RULES } from '@/features/TravelCards/constants';
import { useFormHandling, useNotification, useFormData } from '@/features/common/components/form/composables';
import { useMainStore } from '@/features/common/stores';

const props = defineProps<IFormProps>();
const emit = defineEmits(['update:isVisible', 'submit-success', 'cancel', 'update:travelCardName']);

const { t } = useI18n();

const initialFormState = ref<Omit<UpdateTravelCardDto, 'id'>>({
    name: props.travelCardName || ''
});

const { form, isSubmitting, toggleIsSubmitting, validateOnInput, isFormValid, resetFormValidation } = useFormHandling();
const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const { formData, resetFormData } = useFormData(initialFormState.value);
const mainStore = useMainStore();

const saveData = async (data: UpdateTravelCardDto) => {
    try {
        await updateTravelCard(data.id, data.name);
        emit('update:travelCardName', data.name);
        notificationBarState.value = notificationSuccess(t('travelCard.travelCardUpdatedSuccess'));
        return true;
    } catch (error: any) {
        notificationBarState.value = notificationError(t('travelCard.travelCardUpdatedFailed'));
        mainStore.registerError(error);
        return false;
    }
};

const submitForm = async (name: string) => {
    try {
        toggleIsSubmitting(true);
        const result = await saveData({ name, id: props.travelCardId! });
        if (result) {
            initialFormState.value.name = name;
            emit('submit-success');
            resetFormData(form);
            emit('update:isVisible', false);
        }
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    if (!isFormValid) return;
    await submitForm(formData.value.name);
};

const handleCancel = () => {
    resetFormData(form);
    resetFormValidation();
    emit('cancel');
    emit('update:isVisible', false);
};
</script>
